/* eslint-disable prettier/prettier */
import 'draft-js/dist/Draft.css';

import { CompositeDecorator, EditorState, getDefaultKeyBinding, Modifier, SelectionState } from 'draft-js';
import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { scrollIntoView } from 'seamless-scroll-polyfill';

import { UserPlaceholderImg } from '../../assets';
import { CloseIcon, ImagePlaceholderIcon, SendIcon } from '../../assets';
import AddFileIcon from '../../assets/AddFileIcon';
import EmptyChatIcon from '../../assets/EmptyChatIcon';
import { useMessagesContext } from '../../contexts/MessagesContext';
import { useSessionContext } from '../../contexts/SessionContext';
import { useWindowDimensions } from '../../hooks/useDimensions';
import { Organization, RoomType, useMentionableUsersQuery, UserShort } from '../../types/api.graphql';
import { cancelStorage } from '../../utils/Amplify';
import { createMentionString, MentionEntity } from '../../utils/chat';
import { dateIsFromToday, dateIsFromYesterday, dateIsLessThanWeekAgo, getDayOfWeekOfDate } from '../../utils/date';
import {
  getFileFromChatMessage,
  getImageFromChatMessage,
  getPdfFromChatMessage,
  getTextFromChatMessage,
  messagesFileCloseTag,
  messagesFileOpenTag,
  messagesImgsCloseTag,
  messagesImgsOpenTag,
  messagesPdfCloseTag,
  messagesPdfOpenTag,
  ReceiverType,
} from '../../utils/messages';
import UsersActivityView from '../../views/listings/ListingsPage/DetailsPage/components/UsersActivityView';
import { ActiveRoomDataType } from '../activity/ActivityPanel/ActivityPanel';
import FileUploader from '../FileUploader';
import { ShimmerImage, ShimmerText, ShimmerTextWrapper } from '../Shimmer';
import { TwoEntitiesChatHeaderProps } from '../SingleChat/GeneralEntitiesHeader';
import { GroupChatHeader } from '../SingleChat/GroupHeader';
import VehicleAndMemberHeader, { VehicleAndMemberChatHeader } from '../SingleChat/VehicleAndMemberHeader';
import {
  ActionButton,
  BackButtonContainer,
  ChatBody,
  ChatBodyEmptyPlaceholder,
  ChatBodyEmptyPlaceholderSubtitle,
  ChatBodyEmptyPlaceholderTitle,
  ChatEmptyInfoContainer,
  ChatHeader,
  ChatHeaderContent,
  ChatHeaderLeftContainer,
  ChatHeaderTitle,
  ChatImageLoading,
  ChatImagePreview,
  ChatImagePreviewContainer,
  ChatImageProgressBar,
  ChatImageProgressBarFilled,
  ChatInputBox,
  ChatInputButtons,
  ChatPDFLoading,
  CloseButtonContainer,
  Container,
  ConversationDateDivider,
  ConversationTopViewContainer,
  DefaultMentionUserImageContainer,
  DraftWrapper,
  EmptyChatView,
  FooterContainer,
  InputShimmer,
  MentionAvatar,
  MentionEmail,
  MentionInfo,
  MentionItem,
  MentionName,
  MentionsBottomSheet,
  MessagesContainer,
  MessagesDateBlock,
  MessagesShimmerContainer,
  MobileActionButton,
  MobileSendButton,
  MobileShimmerText,
  NewMessagesPill,
  NoMentionsFound,
  PdfIconContainer,
  SendButton,
  StyledBackArrowIcon,
  StyledCSVFileIcon,
  StyledEditor,
  StyledFileIcon,
  StyledForm,
  StyledInfiniteScroll,
  StyledMessageBubble,
  StyledPDFFileIcon,
} from './SingleChatV2.styles';

export interface ChatEntity {
  id: string;
  type: ReceiverType;
  name?: string;
  imageUrl?: string;
  secondName?: string;
  DefaultImg?: JSX.Element;
}

export type RoomData = ActiveRoomDataType;

interface MessageNode {
  createdAt: any;
  id: string;
  receiverId?: string;
  receiverType?: string;
  sentAt: any;
  text: string;
  sender: {
    id: string;
    firstName?: string;
    email?: string;
    lastName?: string;
    imageUrl?: string;
    organizations?: {
      id: string;
      name?: string;
      imageUrl?: string;
    }[];
  };
  room?: {
    type?: string;
    ownerOrganization?: {
      id: string;
    };
  };
  senderOrganizationId?: string;
}

export type FunctionUserConnected = (userId: string) => boolean;

interface SingleChatProps {
  onSubmit: (message: string) => Promise<void> | undefined;
  onClose: () => void;
  vehicleAndMemberHeader?: VehicleAndMemberChatHeader;
  groupHeader?: GroupChatHeader;
  twoEntitiesHeader?: TwoEntitiesChatHeaderProps;
  messages?: Array<MessageNode>;
  loadMoreMessages?: () => void;
  hasMoreMessages?: boolean;
  loadingMessages?: boolean;
  conversationTopView?: ReactNode;
  isFloatingWindow?: boolean;
  closable?: boolean;
  isShimmer?: boolean;
  isMessageSending?: (sentAt: string) => boolean;
  hasNewMessage?: boolean;
  setHasNewMessage?: (value: boolean) => void;
  roomData?: RoomData;
  roomType?: RoomType;
  isUserConnected?: FunctionUserConnected;
}

const INFINITE_SCROLL_ID = 'infiniteScroll';
const DATE_DIVIDER_CLASS_NAME = 'dateDivider';
const BUBBLE_MESSAGE_HEIGHT = 68;
const MIN_EDITOR_HEIGHT = 34;
const MAX_EDITOR_HEIGHT = 116;
const FOOTER_TOTAL_PADDING = 24;

const ShimmerChatInputBox = () => {
  return (
    <FooterContainer>
      <ChatInputBox>
        <MobileShimmerText $width="2.5rem" $height="2.5rem" $borderRadius="1.25rem" />
        <InputShimmer $flex="1" />
        <ChatInputButtons>
          <ShimmerText $width="2rem" $height="2rem" $borderRadius="1rem" />
          <ShimmerText $width="3.75rem" $height="2rem" $borderRadius="2.125rem" />
        </ChatInputButtons>
      </ChatInputBox>
    </FooterContainer>
  );
};

interface PreviewComponentProps {
  uploadedFileURL: string;
  uploadingFile: boolean;
  uploadingFileProgress: number;
  handleRemoveFile: () => void;
  handleCancelFileUpload: () => void;
  showInMobile?: boolean;
  fileType?: string | null;
}

const FilePreviewComponent = ({
  uploadedFileURL,
  uploadingFile,
  uploadingFileProgress,
  handleRemoveFile,
  handleCancelFileUpload,
  fileType,
  ...rest
}: PreviewComponentProps) => {
  const getFileIcon = () => {
    if (!fileType) return <ImagePlaceholderIcon />;

    if (fileType.startsWith('image/')) {
      return <ImagePlaceholderIcon style={{ marginTop: '0.5rem' }} />;
    }

    if (fileType === 'application/pdf') {
      return <StyledPDFFileIcon />;
    }

    if (fileType.includes('csv') || fileType === 'text/csv') {
      return <StyledCSVFileIcon />;
    }

    return <StyledFileIcon />;
  };

  return (
    <ChatImagePreviewContainer {...rest}>
      {uploadedFileURL && (
        <>
          {fileType?.startsWith('image/') ? (
            <ChatImagePreview src={uploadedFileURL} />
          ) : (
            <ChatPDFLoading>
              <PdfIconContainer>
                {getFileIcon()}
              </PdfIconContainer>
              <ChatImageProgressBar>
                <ChatImageProgressBarFilled percentage={uploadingFileProgress} />
              </ChatImageProgressBar>
            </ChatPDFLoading>
          )}
          <CloseIcon id="close-icon" onClick={() => handleRemoveFile()} />
        </>
      )}
      {!uploadedFileURL && uploadingFile && (
        <>
          <ChatImageLoading>
            <PdfIconContainer>{getFileIcon()}</PdfIconContainer>
            <ChatImageProgressBar>
              <ChatImageProgressBarFilled percentage={uploadingFileProgress} />
            </ChatImageProgressBar>
          </ChatImageLoading>
          <CloseIcon id="close-icon" onClick={() => handleCancelFileUpload()} />
        </>
      )}
    </ChatImagePreviewContainer>
  );
};

export const ShimmerMessages = () => {
  return (
    <>
      <StyledMessageBubble isLoggedUserSide={false} isShimmer minWidth="80%" />
      <StyledMessageBubble isLoggedUserSide isShimmer minWidth="80%" />
      <StyledMessageBubble isLoggedUserSide={false} isShimmer minWidth="80%" />
    </>
  );
}


const findMentionEntities = (contentBlock: any, callback: any, contentState: any) => {
  contentBlock.findEntityRanges(
    (character: any) => {
      const entityKey = character.getEntity();
      if (entityKey === null) {
        return false;
      }
      const entity = contentState.getEntity(entityKey);
      return entity.getType() === 'mention';
    },
    callback
  );
};

const MentionSpan = (props: any) => {
  return (
    <span className="mention">
      {props.children}
    </span>
  );
};

const SingleChatV2 = ({
  onSubmit,
  onClose,
  vehicleAndMemberHeader,
  groupHeader,
  twoEntitiesHeader,
  messages,
  loadMoreMessages,
  hasMoreMessages,
  loadingMessages,
  conversationTopView,
  isFloatingWindow = true,
  closable = true,
  isShimmer,
  isMessageSending,
  hasNewMessage,
  setHasNewMessage,
  roomData,
  roomType,
  isUserConnected,
}: SingleChatProps) => {
  // Dependencies
  const { me } = useSessionContext();
  const { t } = useTranslation();
  const { isMessageError, reSendMessage, creatingMessage, usersActivity } = useMessagesContext();
  const { isInTablet } = useWindowDimensions();

  // State
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingImageProgress, setUploadingImageProgress] = useState(0);
  const [uploadedFileURL, setUploadedFileURL] = useState(null);
  const [loadingMessagesStarted, setLoadingMessagesStarted] = useState(false);
  const [hideScroll, setHideScroll] = useState(false);
  const [firstMessageSending, setFirstMessageSending] = useState<MessageNode>(undefined);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionUsers, setMentionUsers] = useState<UserShort[]>([]);
  const [editorState, setEditorState] = useState(() => {
    const compositeDecorator = new CompositeDecorator([
      {
        strategy: (contentBlock: any, callback: any, contentState: any) =>
          findMentionEntities(contentBlock, callback, contentState),
        component: MentionSpan,
      },
    ]);
    return EditorState.createEmpty(compositeDecorator);
  });

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const uploadOperationRef = useRef<any | undefined>(undefined);
  const scrollTimerRef = useRef<any>();

  // Track editor height
  const [editorHeight, setEditorHeight] = useState(MIN_EDITOR_HEIGHT);
  const draftWrapperRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const messagesLocal = messages ?? (firstMessageSending ? [firstMessageSending] : undefined);

  // State to track file type
  const [uploadedFileType, setUploadedFileType] = useState<string | null>(null);

  // State for tracking file type during upload
  const [currentFileType, setCurrentFileType] = useState<string | null>(null);

  const handleFileTypeChange = (fileType: string) => {
    setCurrentFileType(fileType);
  };

  const handleUploadFinish = (url: string, fileType: string) => {
    setUploadedFileURL(url);
    setUploadedFileType(fileType);
    setCurrentFileType(fileType);
    setUploadingImageProgress(0);
  };

  const handleRemoveFile = () => {
    fileInputRef.current.value = '';
    setUploadedFileURL(null);
    setUploadedFileType(null);
    setCurrentFileType(null);
    setUploadingImage(false);
  };

  const handleCancelImageUpload = () => {
    void (async () => {
      await cancelStorage(uploadOperationRef?.current);
      fileInputRef.current.value = '';
      setUploadedFileURL(null);
      setUploadedFileType(null);
      setCurrentFileType(null);
      setUploadingImage(false);
      setUploadingImageProgress(0);
    })();
  };

  // Effects
  useEffect(() => {
    if (!loadingMessagesStarted && loadingMessages) {
      setLoadingMessagesStarted(true);
    } else if (loadingMessagesStarted && !loadingMessages) {
      setHideScroll(true);
      setLoadingMessagesStarted(false);
      setTimeout(() => {
        scrollToBottom();
        setHideScroll(false);
      }, 200);
    }
  }, [loadingMessages, loadingMessagesStarted]);

  useEffect(() => {
    const messageBox = document.getElementById(INFINITE_SCROLL_ID);
    const scrollEventFunction = () => {
      const dateDividers = document.getElementsByClassName(DATE_DIVIDER_CLASS_NAME);
      // The setTimeOut will be executed a short time after the user stops scrolling to hide the floating date
      if (scrollTimerRef.current !== null) {
        clearTimeout(scrollTimerRef.current);
      }
      scrollTimerRef.current = null;
      scrollTimerRef.current = setTimeout(function () {
        if (messageBox?.scrollTop !== 0) {
          Array.from(dateDividers).forEach((divider) => {
            const dateDivider = divider as HTMLElement;
            // Hide if the sticky position is more than 0px in MessagesDateBlock
            if (dateDivider.offsetTop !== 0) {
              dateDivider.style.visibility = 'hidden';
            }
          });
        }
      }, 200);
      Array.from(dateDividers).forEach((divider) => {
        const dateDivider = divider as HTMLElement;
        dateDivider.style.visibility = 'visible';
      });
    };
    messageBox?.addEventListener?.('scroll', scrollEventFunction);

    return () => messageBox?.removeEventListener?.('scroll', scrollEventFunction);
  }, []);

  useEffect(() => {
    if (!creatingMessage && firstMessageSending && messages) {
      setFirstMessageSending(undefined);
    }
  }, [creatingMessage, firstMessageSending, messages]);

  // Reset editor state when conversation changes
  useEffect(() => {
    // Create new empty editor state with the same decorator
    const compositeDecorator = new CompositeDecorator([
      {
        strategy: (contentBlock: any, callback: any, contentState: any) =>
          findMentionEntities(contentBlock, callback, contentState),
        component: MentionSpan,
      },
    ]);

    setEditorState(EditorState.createEmpty(compositeDecorator));
    setShowMentions(false);
    setMentionUsers([]);

    // Reset image upload state if needed
    setUploadingImage(false);
    setUploadingImageProgress(0);
    setUploadedFileURL(null);

    // Reset editor height
    setEditorHeight(MIN_EDITOR_HEIGHT);
  }, [roomData?.id]);

  // Update the effect to measure wrapper height
  useEffect(() => {
    const measureEditorHeight = () => {
      if (draftWrapperRef.current) {
        const editorElement = draftWrapperRef.current.querySelector('.DraftEditor-root');
        if (editorElement) {
          // Clamp the height between MIN_EDITOR_HEIGHT and MAX_EDITOR_HEIGHT
          const height = Math.min(
            MAX_EDITOR_HEIGHT,
            Math.max(MIN_EDITOR_HEIGHT, editorElement.scrollHeight)
          );
          setEditorHeight(height);
        }
      }
    };

    // Create ResizeObserver only once if it doesn't exist
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        measureEditorHeight();
      });
    }

    // Initial measurement
    measureEditorHeight();

    // Start observing
    if (draftWrapperRef.current) {
      resizeObserverRef.current.observe(draftWrapperRef.current);
    }

    return () => {
      // Clean up by unobserving the current element
      if (draftWrapperRef.current && resizeObserverRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resizeObserverRef.current.unobserve(draftWrapperRef.current);
      }
    };
  }, [editorState]); // Keep editorState as dependency to re-measure when content changes

  // Clean up the observer when component unmounts
  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, []);

  // Add keyboard event handler to close mentions with Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showMentions) {
        setShowMentions(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showMentions]);

  // When mentions open in tablet, only allow scrolling inside the bottomsheet
  useEffect(() => {
    // Only apply on tablet
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    if (!isInTablet) return () => { };

    let originalScrollY = 0;

    if (showMentions) {
      // Save current scroll position
      originalScrollY = window.scrollY;

      // Lock scroll
      document.documentElement.style.position = 'fixed';
      document.documentElement.style.top = `-${originalScrollY}px`;
      document.documentElement.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';

      // Set up the mentions sheet for scrolling
      const mentionsSheet = document.querySelector('[data-mentions-sheet]');
      if (mentionsSheet) {
        (mentionsSheet as HTMLElement).style.position = 'fixed';
        (mentionsSheet as HTMLElement).style.bottom = '0';
        (mentionsSheet as HTMLElement).style.left = '0';
        (mentionsSheet as HTMLElement).style.right = '0';
        (mentionsSheet as HTMLElement).style.height = '50vh';
        (mentionsSheet as HTMLElement).style.overflowY = 'scroll';
      }
    }

    return () => {
      if (showMentions) {
        // Reset mentions sheet styles
        const mentionsSheet = document.querySelector('[data-mentions-sheet]');
        if (mentionsSheet) {
          (mentionsSheet as HTMLElement).style.position = '';
          (mentionsSheet as HTMLElement).style.bottom = '';
          (mentionsSheet as HTMLElement).style.left = '';
          (mentionsSheet as HTMLElement).style.right = '';
          (mentionsSheet as HTMLElement).style.height = '';
          (mentionsSheet as HTMLElement).style.overflowY = '';
        }

        // Restore scroll
        document.documentElement.style.position = '';
        document.documentElement.style.top = '';
        document.documentElement.style.width = '';
        document.documentElement.style.overflow = '';

        // Restore scroll position
        window.scrollTo(0, originalScrollY);
      }
    };
  }, [showMentions, isInTablet]);

  // Helpers
  const renderDate = (date: string) => {
    const dateValue = new Date(date);
    const momentDate = moment(date);

    switch (true) {
      case dateIsFromToday(dateValue):
        return t('components.chat.today')
      case dateIsFromYesterday(dateValue):
        return t('components.chat.yesterday')
      case dateIsLessThanWeekAgo(dateValue):
        return getDayOfWeekOfDate(dateValue);
      default:
        return momentDate.format('MM/DD/YYYY');
    }
  };

  const renderMessagesBlock = (messages: MessageNode[], roomData: RoomData) => {
    const getMessageOrganizationAvatar = (message: MessageNode): Organization | undefined => {
      const senderOrganizationId = message.senderOrganizationId;
      const entity1 = roomData?.entity1;
      const entity2 = roomData?.entity2;
      if (!senderOrganizationId) return undefined;
      if (entity1?.id === senderOrganizationId && entity1?.type === ReceiverType.Organization) {
        return entity1;
      } else {
        return entity2;
      }
    }

    const isLoggedUserSide = (message: MessageNode): boolean => {
      const senderOrganizationId = message.senderOrganizationId;
      const entity1 = roomData?.entity1;
      if (roomType !== RoomType.ListingInternalRoom) {
        // Note: entity1 always refer to the logged user
        if (senderOrganizationId) {
          return entity1?.id === senderOrganizationId;
        }
        if (entity1?.type === ReceiverType.Organization) {
          return false;
        }
      }
      return me?.id === message?.sender?.id;
    };

    return messages?.map((message) => (
      <StyledMessageBubble
        key={message.id}
        user={{
          name: message.sender?.firstName,
          lastName: message.sender?.lastName,
          profileImage: message.sender?.imageUrl,
          organization: getMessageOrganizationAvatar(message),
        }}
        message={{
          text: getTextFromChatMessage(message.text),
          timestamp: message.createdAt,
          image: getImageFromChatMessage(message.text),
          pdf: getPdfFromChatMessage(message.text),
          file: getFileFromChatMessage(message.text),
          sentAt: message.sentAt
        }}
        isGroupChat={groupHeader || vehicleAndMemberHeader ? true : false}
        isLoggedUserSide={isLoggedUserSide(message)}
        isSending={!!firstMessageSending || isMessageSending(message.sentAt)}
        isError={isMessageError(message.sentAt)}
        isUserConnected={usersActivity?.some(user => user.userId === message.sender?.id && user.isConnected) || isUserConnected(message.sender?.id)}
        onResend={reSendMessage}
        onParseMention={handleParseMention}
      />
    ));
  };

  const [newMsgInScrollRange, setNewMsgInScrollRange] = useState(false);
  const onScroll = (event: any) => {
    const { scrollHeight, scrollTop, clientHeight } = event.target;
    const scroll = scrollHeight - scrollTop - clientHeight;
    if (scroll > BUBBLE_MESSAGE_HEIGHT) {
      // if it scrolls up more than 1 message height then show bubble
      setNewMsgInScrollRange(true);
    } else {
      // if user scrolls down manually and doesn't click the bubble then remove it
      setNewMsgInScrollRange(false);
      setHasNewMessage?.(false);
    }
  };

  const scrollToBottom = (smooth?: boolean) => {
    if (messagesEndRef?.current) {
      const defaultScrollOptions: any = { block: 'nearest', inline: 'start' };
      const scrollOptions: any = smooth ? { ...defaultScrollOptions, behavior: 'smooth' } : defaultScrollOptions;
      scrollIntoView(messagesEndRef.current, scrollOptions);
    }
  };

  // Handlers
  const handleUploadProgressCallback = (progress) => {
    setUploadingImageProgress(100 * (progress.loaded / progress.total));
  };

  const handleUploadImageClick = () => {
    if (!uploadedFileURL && !uploadingImage) {
      fileInputRef?.current.click();
    } else {
      toast.error(t('components.chat.oneImageUploadError'));
    }
  };

  const handleSubmit = () => {
    let fileText = '';
    let messageText = '';

    if (uploadedFileURL && !uploadingImage) {
      const isImage = uploadedFileType?.startsWith('image/');
      const isPdf = uploadedFileType === 'application/pdf';

      if (isImage) {
        fileText = `${messagesImgsOpenTag}${uploadedFileURL}${messagesImgsCloseTag}`;
      } else if (isPdf) {
        fileText = `${messagesPdfOpenTag}${uploadedFileURL}${messagesPdfCloseTag}`;
      } else {
        fileText = `${messagesFileOpenTag}${uploadedFileURL}${messagesFileCloseTag}`;
      }
    }

    if (editorState.getCurrentContent().hasText()) {
      const plainText = editorState.getCurrentContent().getPlainText();
      messageText = transformMessage(plainText);
    }

    if (!messages) {
      setFirstMessageSending({
        id: '-1',
        createdAt: new Date(),
        sentAt: new Date(),
        room: {
          type: roomData.type,
        },
        sender: me,
        senderOrganizationId: roomData?.entity1?.id,
        text: messageText,
      });
    }
    void onSubmit([fileText, messageText].join(''));

    // Create new empty state with the existing decorator
    const newEditorState = EditorState.createEmpty(editorState.getDecorator());

    // Clear states
    setEditorState(newEditorState);
    setUploadedFileURL(null);
    setTimeout(() => {
      scrollToBottom();
    });
  };

  const handleChatCloseClick = () => {
    onClose?.();
  };

  const showNewMessages = () => {
    scrollToBottom(true);
    setHasNewMessage(false);
  };

  // Derived State
  const groupedDays = (messages: MessageNode[]): Record<string, MessageNode[]> => {
    return messages?.reduce((acc, el) => {
      const messageDay = renderDate(el.createdAt);
      if (acc[messageDay]) {
        return { ...acc, [messageDay]: acc[messageDay].concat([el]) };
      }
      return { ...acc, [messageDay]: [el] };
    }, {});
  };

  const groupedMessages = useMemo(() => {
    return messagesLocal ? groupedDays(messagesLocal) : {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesLocal]);


  const isLoading = isShimmer || loadingMessages;
  const chatWithNonexistentEntity = roomData?.entity2 && !roomData?.entity2?.id;
  const shouldShowEmptyView = !isLoading && (!messagesLocal || messagesLocal.length === 0);
  const shouldShowShimmer = isLoading && !firstMessageSending;
  // hide users activity for an internal room
  const shouldShowUsersActivity = roomData?.type !== RoomType.ListingInternalRoom;
  // show 4 avatar shimmers for organizatins rooms and 1 for user ones
  const shimmersCount = roomData?.entity2?.type === ReceiverType.User || chatWithNonexistentEntity ? 1 : 4;

  const { data: mentionableUsersData, loading: loadingMentions } = useMentionableUsersQuery({
    variables: {
      roomType: roomData?.type,
      listingId: roomData?.listingId,
    },
    skip: !roomData?.listingId || !roomData?.type,
  });

  const handleEditorChange = (newState: EditorState) => {
    // Prevent scroll changes during editor update
    const windowScrollY = window.scrollY;

    const preventScroll = () => {
      window.scrollTo(0, windowScrollY);
    };

    window.addEventListener('scroll', preventScroll, { passive: false });
    setEditorState(newState);

    requestAnimationFrame(() => {
      window.removeEventListener('scroll', preventScroll);
      window.scrollTo(0, windowScrollY);
    });

    const selection = newState.getSelection();
    const content = newState.getCurrentContent();
    const currentBlock = content.getBlockForKey(selection.getAnchorKey());
    const text = currentBlock.getText();
    const position = selection.getAnchorOffset();

    // Check if we're in a mention search context
    const textBeforeCursor = text.slice(0, position);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    // If backspace is pressed and mentions aren't showing, don't trigger the bottomsheet
    if (newState.getLastChangeType() === 'backspace-character' && !showMentions) {
      return;
    }

    if (lastAtSymbol !== -1) {
      // Check if this @ is part of an existing mention entity
      const entityKey = currentBlock.getEntityAt(lastAtSymbol);
      if (entityKey) {
        setShowMentions(false);
        return;
      }

      // Check if @ is part of an email or word
      const beforeAt = text.slice(0, lastAtSymbol);
      const isPartOfWord = /\S$/.test(beforeAt);

      if (!isPartOfWord) {
        const textAfterAt = textBeforeCursor.slice(lastAtSymbol);
        const hasEnterAfterMention = textAfterAt.includes('\n');
        const mentionText = textAfterAt.slice(1); // Remove the @ symbol

        // Changed conditions to allow spaces in search
        if (hasEnterAfterMention || mentionText.length > 50) {
          setShowMentions(false);
        } else {
          setShowMentions(true);

          // Update filtering to handle spaces
          const filtered = mentionableUsersData?.mentionableUsers?.filter((user) => {
            const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
            const searchTerms = mentionText.toLowerCase().split(' ');

            // Match all search terms against the full name
            return searchTerms.every(term => fullName.includes(term));
          }) ?? [];

          setMentionUsers(filtered);
        }
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (user: UserShort) => {
    const displayText = `@${user.firstName} ${user.lastName}`;
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const currentBlock = contentState.getBlockForKey(selection.getAnchorKey());
    const text = currentBlock.getText();
    const position = selection.getAnchorOffset();
    const textBeforeCursor = text.slice(0, position);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Create the selection for the mention text
      const mentionSelection = SelectionState.createEmpty(currentBlock.getKey())
        .merge({
          anchorOffset: lastAtIndex,
          focusOffset: position,
          anchorKey: currentBlock.getKey(),
          focusKey: currentBlock.getKey(),
        });

      // Create a mention entity
      const contentStateWithEntity = contentState.createEntity(
        'mention',
        'IMMUTABLE',
        {
          displayText,
          entityType: 'user',
          entityId: user.id,
          displayName: `${user.firstName} ${user.lastName}`
        }
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      // Replace the text with mention
      let newContentState = Modifier.replaceText(
        contentStateWithEntity,
        mentionSelection,
        displayText,
        undefined,
        entityKey
      );

      // Add a space after mention
      const spaceSelection = mentionSelection.merge({
        anchorOffset: lastAtIndex + displayText.length,
        focusOffset: lastAtIndex + displayText.length,
      });

      newContentState = Modifier.insertText(
        newContentState,
        spaceSelection,
        ' '
      );

      // Push the new content state
      let newEditorState = EditorState.push(
        editorState,
        newContentState,
        'insert-characters'
      );

      // Move cursor after the space
      const finalSelection = spaceSelection.merge({
        anchorOffset: lastAtIndex + displayText.length + 1,
        focusOffset: lastAtIndex + displayText.length + 1,
      });

      newEditorState = EditorState.forceSelection(newEditorState, finalSelection);
      setEditorState(newEditorState);
      setShowMentions(false);
    }
  };

  // Handle keydown events
  const handleEditorKeyCommand = (command: string) => {
    if (command === 'backspace') {
      const selection = editorState.getSelection();
      if (selection.isCollapsed()) {
        const content = editorState.getCurrentContent();
        const block = content.getBlockForKey(selection.getStartKey());
        const offset = selection.getStartOffset();
        const entityKey = block.getEntityAt(offset - 1);

        if (entityKey) {
          const entity = content.getEntity(entityKey);
          if (entity.getType() === 'mention') {
            // Find the start and end of the mention
            let mentionStart = offset - 1;
            while (mentionStart > 0 && block.getEntityAt(mentionStart - 1) === entityKey) {
              mentionStart--;
            }

            let mentionEnd = offset;
            while (mentionEnd < block.getLength() && block.getEntityAt(mentionEnd) === entityKey) {
              mentionEnd++;
            }

            // Check if there's a space after the mention
            if (block.getText()[mentionEnd] === ' ') {
              mentionEnd++;
            }

            const mentionSelection = SelectionState.createEmpty(block.getKey())
              .merge({
                anchorOffset: mentionStart,
                focusOffset: mentionEnd,
                anchorKey: block.getKey(),
                focusKey: block.getKey(),
              });

            const newContent = Modifier.removeRange(content, mentionSelection, 'backward');
            const newEditorState = EditorState.push(editorState, newContent, 'remove-range');

            const finalSelection = mentionSelection.merge({
              anchorOffset: mentionStart,
              focusOffset: mentionStart,
            });

            setEditorState(EditorState.forceSelection(newEditorState, finalSelection));
            return 'handled';
          }
        }
      }
    }
    return 'not-handled';
  };

  const handleParseMention = (entity: MentionEntity): string => {
    switch (entity.type) {
      case 'user':
        return `@${entity.displayName}`;
      // Add more entity types as needed
      default:
        return `@${entity.displayName}`;
    }
  };

  // Update the transformMessage function to use the new utility
  const transformMessage = (text: string): string => {
    let transformedText = text;
    const contentState = editorState.getCurrentContent();
    const blocks = contentState.getBlocksAsArray();

    blocks.forEach(block => {
      const entityRanges: { start: number; end: number; key: string }[] = [];
      block.findEntityRanges(
        (character) => {
          const entityKey = character.getEntity();
          return entityKey !== null && contentState.getEntity(entityKey).getType() === 'mention';
        },
        (start, end) => {
          const entityKey = block.getEntityAt(start);
          entityRanges.push({ start, end, key: entityKey });
        }
      );

      entityRanges.sort((a, b) => b.start - a.start);

      entityRanges.forEach(range => {
        const entity = contentState.getEntity(range.key);
        const { entityType, entityId, displayName } = entity.getData();
        const mentionText = block.getText().slice(range.start, range.end);
        transformedText = transformedText.replace(
          mentionText,
          createMentionString(entityType, entityId, displayName)
        );
      });
    });

    return transformedText;
  };

  const MentionShimmerList = () => {
    return (
      <>
        {[...Array(6)].map((i) => (
          <MentionItem key={i}>
            <ShimmerImage $width="2rem" $height="2rem" $borderRadius="50%" $marginRight="0.5rem" />
            <MentionInfo>
              <ShimmerText $width="12rem" $height="1rem" $marginBottom="0.25rem" />
              <ShimmerText $width="12rem" $height="0.75rem" />
            </MentionInfo>
          </MentionItem>
        ))}
      </>
    );
  };

  const keyBindingFn = (e: React.KeyboardEvent<object>): string | null => {
    if (e.key === 'Enter') {
      if (!e.shiftKey) {
        // Regular enter - should send message
        e.preventDefault();
        if (uploadedFileURL || editorState.getCurrentContent().hasText()) {
          handleSubmit();
        }
        // Return 'handled' tells Draft.js we've handled this key event ourselves
        // and it should not perform any additional actions
        return 'handled';
      }
      // Return 'split-block' tells Draft.js to insert a new line
      // This is Draft.js's internal command for creating line breaks
      return 'split-block';
    }
    return getDefaultKeyBinding(e);
  };

  return (
    <Container $isFloatingWindow={isFloatingWindow}>
      <ChatHeader>
        <BackButtonContainer onClick={onClose}>
          <StyledBackArrowIcon />
        </BackButtonContainer>
        <ChatHeaderContent>
          <VehicleAndMemberHeader data={vehicleAndMemberHeader} />
          <ShimmerTextWrapper loading={shouldShowShimmer} $width="9rem" $flex="1">
            <ChatHeaderLeftContainer>
              <ChatHeaderTitle>{roomData?.title}</ChatHeaderTitle>
              {chatWithNonexistentEntity && twoEntitiesHeader?.subtitle}
            </ChatHeaderLeftContainer>
          </ShimmerTextWrapper>
          {shouldShowUsersActivity &&
            <UsersActivityView
              loading={shouldShowShimmer || !usersActivity}
              users={usersActivity || []}
              maxAvatarsCount={3}
              shimmersCount={shimmersCount}
              tooltipPlacement="bottomEnd"
            />
          }
        </ChatHeaderContent>
        {closable && (
          <CloseButtonContainer onClick={handleChatCloseClick}>
            <CloseIcon />
          </CloseButtonContainer>
        )}
      </ChatHeader>
      {conversationTopView && <ConversationTopViewContainer>{conversationTopView}</ConversationTopViewContainer>}
      <ChatBody $hasMoreMessages={hasMoreMessages} $hideScroll={hideScroll} onScroll={onScroll} id={INFINITE_SCROLL_ID}>
        {shouldShowEmptyView && (
          <EmptyChatView>
            <ChatBodyEmptyPlaceholder>
              <EmptyChatIcon />
              <ChatEmptyInfoContainer>
                <ChatBodyEmptyPlaceholderTitle>
                  {chatWithNonexistentEntity ? t('components.chat.emptyChatTitleWithoutUser') : t('components.chat.emptyChatPlaceholderTitle')}
                </ChatBodyEmptyPlaceholderTitle>
                <ChatBodyEmptyPlaceholderSubtitle>
                  {chatWithNonexistentEntity ? t('components.chat.emptyChatSubtitleWithoutUser') : t('components.chat.emptyChatPlaceholderSubtitle')}
                </ChatBodyEmptyPlaceholderSubtitle>
              </ChatEmptyInfoContainer>
            </ChatBodyEmptyPlaceholder>
          </EmptyChatView>
        )}
        {shouldShowShimmer ? (<MessagesShimmerContainer><ShimmerMessages /></MessagesShimmerContainer>) : messagesLocal && (
          <StyledInfiniteScroll
            initialLoad={false}
            loadMore={loadMoreMessages}
            hasMore={hasMoreMessages}
            useWindow={false}
            isReverse={true}
            loader={<ShimmerMessages />}
            threshold={250}
          >
            <MessagesContainer>
              {Object.keys(groupedMessages).map((key) => {
                return (
                  <MessagesDateBlock>
                    {key.length > 0 && (
                      <ConversationDateDivider className={DATE_DIVIDER_CLASS_NAME}>{key}</ConversationDateDivider>
                    )}
                    {renderMessagesBlock(groupedMessages[key], roomData)}
                  </MessagesDateBlock>
                );
              })}
              <div ref={messagesEndRef} />
            </MessagesContainer>
          </StyledInfiniteScroll>
        )}
      </ChatBody>
      {shouldShowShimmer ? (<ShimmerChatInputBox />) :
        (<FooterContainer>
          <FilePreviewComponent
            uploadedFileURL={uploadedFileURL}
            uploadingFile={uploadingImage}
            uploadingFileProgress={uploadingImageProgress}
            handleRemoveFile={handleRemoveFile}
            handleCancelFileUpload={handleCancelImageUpload}
            fileType={currentFileType || uploadedFileType}
          />
          <ChatInputBox>
            <MobileActionButton onClick={handleUploadImageClick}>
              <FileUploader
                inputRef={fileInputRef}
                setLoading={setUploadingImage}
                onUploadFinish={handleUploadFinish}
                onFileTypeChange={handleFileTypeChange}
                accept="image/*,.pdf,.csv,.docx,.xlsx,.pptx,.txt,.pdf,.doc,.xls,.ppt,.txt,.csv"
                uploadProgressCallback={handleUploadProgressCallback}
                uploadOperationRef={uploadOperationRef}
              />
              <AddFileIcon />
            </MobileActionButton>
            <StyledForm onSubmit={handleSubmit}>
              <DraftWrapper ref={draftWrapperRef} $isMinHeight={editorHeight === MIN_EDITOR_HEIGHT} $height={editorHeight}>
                <StyledEditor
                  editorState={editorState}
                  onChange={handleEditorChange}
                  placeholder={t('components.chat.chatInputPlaceholder')}
                  handleKeyCommand={handleEditorKeyCommand}
                  keyBindingFn={keyBindingFn}
                />
              </DraftWrapper>
            </StyledForm>
            <ChatInputButtons>
              <ActionButton onClick={handleUploadImageClick}>
                <FileUploader
                  inputRef={fileInputRef}
                  setLoading={setUploadingImage}
                  onUploadFinish={handleUploadFinish}
                  onFileTypeChange={handleFileTypeChange}
                  accept="image/*,.pdf,.csv,.docx,.xlsx,.pptx,.txt,.pdf,.doc,.xls,.ppt,.txt,.csv"
                  uploadProgressCallback={handleUploadProgressCallback}
                  uploadOperationRef={uploadOperationRef}
                />
                <AddFileIcon />
              </ActionButton>
              <SendButton onPress={() => handleSubmit()} disabled={!uploadedFileURL && !editorState.getCurrentContent().hasText()}>
                {t('components.chat.sendButton')}
              </SendButton>
            </ChatInputButtons>
            <NewMessagesPill $visible={hasNewMessage && newMsgInScrollRange} onClick={showNewMessages}>{t('components.chat.newMessages')}</NewMessagesPill>
            <MobileSendButton onTouchEnd={() => handleSubmit()} $visible={uploadedFileURL || editorState.getCurrentContent().hasText()} autoFocus>
              <SendIcon />
            </MobileSendButton>
          </ChatInputBox>
        </FooterContainer>)}
      <AnimatePresence>
        {showMentions && (
          <MentionsBottomSheet
            initial={{ y: "100%", scaleY: 0 }}
            animate={{
              y: 0,
              scaleY: 1,
              transition: {
                type: "spring",
                damping: 25,
                stiffness: 200
              }
            }}
            exit={{
              y: "100%",
              scaleY: 0,
              transition: {
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
              }
            }}
            style={{
              transformOrigin: "bottom",
              bottom: `${Math.min(MAX_EDITOR_HEIGHT, editorHeight) + FOOTER_TOTAL_PADDING}px`
            }}
          >
            {loadingMentions ? (
              <MentionShimmerList />
            ) : mentionUsers.length > 0 ? (
              mentionUsers.map(user => (
                <MentionItem key={user.id} onClick={() => handleMentionSelect(user)}>
                  {user.imageUrl ? (
                    <MentionAvatar src={user.imageUrl} name={`${user.firstName} ${user.lastName}`} />
                  ) : (
                    <DefaultMentionUserImageContainer>{<UserPlaceholderImg />}</DefaultMentionUserImageContainer>
                  )}
                  <MentionInfo>
                    <MentionName>{user.firstName} {user.lastName}</MentionName>
                    <MentionEmail>{user.email}</MentionEmail>
                  </MentionInfo>
                </MentionItem>
              ))
            ) : (
              <NoMentionsFound>{t('components.chat.noUsersFound')}</NoMentionsFound>
            )}
          </MentionsBottomSheet>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default SingleChatV2;
